import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type StateUpdater,
} from "preact/hooks";

export type AsyncData<T> = {
  data: T | null;
  error: string | null;
  loading: boolean;
  // Re-run the fetcher without blanking the current data (no skeleton flash) —
  // for refreshing after a mutation.
  reload: () => void;
  // Escape hatches for optimistic updates and surfacing mutation errors on the
  // same banner the fetch uses.
  setData: Dispatch<StateUpdater<T | null>>;
  setError: Dispatch<StateUpdater<string | null>>;
};

// Guarded async fetch for route-level screens. Centralizes the stale-overwrite
// guard (an earlier fetch resolving after a newer one must not clobber it) and
// the state reset on identity change, so each screen doesn't hand-roll — and
// risk forgetting — the `alive` flag. `deps` are the identity inputs (e.g.
// [projectId]); changing them blanks the data and refetches. `fetcher` is read
// through a ref so callers can pass an inline closure without retriggering.
export function useAsyncData<T>(
  fetcher: () => Promise<T>,
  deps: unknown[],
): AsyncData<T> {
  const [data, setData] = useState<T | null>(null);
  // Identity that the committed `data` was fetched under. The reset effect
  // below only fires post-paint, so without this the render right after a deps
  // change paints the previous project's data for one frame under the new
  // identity. Comparing this to the current identity during render gates that
  // stale frame out. A stable string of `deps` (all call sites pass strings).
  const [dataIdentity, setDataIdentity] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const identity = JSON.stringify(deps);

  // Blank the previous result the moment the identity deps change (e.g. a new
  // projectId) so a stale result can't linger under the new context. Keyed on
  // deps only — not the reload nonce — so a post-mutation reload keeps the
  // current data on screen instead of flashing a skeleton.
  useEffect(() => {
    setData(null);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    // Each run owns its own `alive`: Preact runs this cleanup before the next
    // run (on a deps change, a reload, or unmount), so a fetch that resolves
    // after it was superseded skips its setState.
    let alive = true;
    setLoading(true);
    void (async () => {
      try {
        const result = await fetcherRef.current();
        if (alive) {
          // Tag the data with the identity it belongs to (both updates coalesce
          // into one Preact re-render since they run synchronously here).
          setData(result);
          setDataIdentity(identity);
        }
      } catch (e) {
        if (alive) setError((e as Error).message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  // Gate out data captured under a previous identity so it can't paint for the
  // frame before the reset effect runs. Optimistic `setData` from callers keeps
  // showing because `dataIdentity` already matches the current identity.
  const currentData = dataIdentity === identity ? data : null;
  return { data: currentData, error, loading, reload, setData, setError };
}
